// Lectura del texto de un PDF en el propio navegador (pdf.js). El fichero no se sube a ningún sitio:
// se lee en memoria y se descarta. pdf.js se carga solo cuando hace falta (trozo aparte del bundle).

// Junta los trozos de texto de una página en líneas, por su altura (y) y de izquierda a derecha (x).
export function itemsToLines(items) {
  const rows = [];
  items.forEach(it => {
    const str = (it.str || '').replace(/\s+/g, ' ');
    if (!str.trim()) return;
    const x = it.transform[4];
    const y = it.transform[5];
    let row = rows.find(r => Math.abs(r.y - y) <= 2.5);
    if (!row) { row = { y, parts: [] }; rows.push(row); }
    row.parts.push({ x, str, w: it.width || 0 });
  });
  rows.sort((a, b) => b.y - a.y);
  return rows.map(r => {
    r.parts.sort((a, b) => a.x - b.x);
    let line = '';
    let end = null;
    r.parts.forEach(p => {
      // Separación grande en la misma altura = otra columna: se marca con un tabulador.
      const gap = end == null ? 0 : p.x - end;
      line += end == null ? p.str : (gap > 40 ? '\t' : gap > 1 && !line.endsWith(' ') && !p.str.startsWith(' ') ? ' ' : '') + p.str;
      end = p.x + p.w;
    });
    return line.trim();
  }).filter(Boolean);
}

// Tabla "Otros leads" de la ficha de Flexicar: está girada (una columna por lead y una fila por dato, con los
// títulos de fila a la izquierda) y las celdas estrechas se parten en varias líneas ("27-10-" / "2026"), así que
// el texto línea a línea mezcla columnas. Se rehace por posiciones: fila = título a la misma altura o por encima,
// columna = centro horizontal del texto. Devuelve líneas "OTRO LEAD\tFECHA CREACIÓN=…\tVEHÍCULO=…\tPRECIO=…".
const ROW_LABELS = ['Fecha Creación', 'Última Modificación', 'Estado', 'Procedencia', 'Vehículo', 'Precio', 'Financia', 'Acciones'];
export function otherLeadsLines(items) {
  const labels = items.filter(it => ROW_LABELS.includes((it.str || '').trim()))
    .map(it => ({ key: it.str.trim(), x: it.transform[4], y: it.transform[5], right: it.transform[4] + (it.width || 0) }));
  const need = ['Fecha Creación', 'Vehículo', 'Precio'];
  if (!need.every(k => labels.some(l => l.key === k))) return [];
  labels.sort((a, b) => b.y - a.y);
  const left = Math.min(...labels.map(l => l.x));
  const valuesFrom = Math.max(...labels.map(l => l.right)) + 4;
  const top = labels[0].y + 3;
  const bottom = labels[labels.length - 1].y - 3;
  const cells = [];
  items.forEach(it => {
    const str = (it.str || '').trim();
    const x = it.transform[4]; const y = it.transform[5];
    if (!str || x < valuesFrom || x < left || y > top || y < bottom) return;
    const row = labels.find((l, i) => y <= l.y + 3 && (i === labels.length - 1 || y > labels[i + 1].y + 3));
    if (!row) return;
    cells.push({ row: row.key, cx: x + (it.width || 0) / 2, x, y, str });
  });
  // Columnas: centros horizontales cercanos (las celdas van centradas).
  const cols = [];
  cells.sort((a, b) => a.cx - b.cx).forEach(c => {
    const col = cols.find(k => Math.abs(k.cx - c.cx) <= 18);
    if (col) { col.cells.push(c); col.cx = (col.cx + c.cx) / 2; } else cols.push({ cx: c.cx, cells: [c] });
  });
  const text = (col, key) => col.cells.filter(c => c.row === key)
    .sort((a, b) => (b.y - a.y) || (a.x - b.x))
    .reduce((acc, c) => (acc ? (acc.endsWith('-') ? acc + c.str : `${acc} ${c.str}`) : c.str), '');
  return cols.sort((a, b) => a.cx - b.cx).map(col =>
    `OTRO LEAD\tFECHA CREACIÓN=${text(col, 'Fecha Creación')}\tVEHÍCULO=${text(col, 'Vehículo')}\tPRECIO=${text(col, 'Precio')}`);
}

export async function extractPdfText(file, { maxPages = 5 } = {}) {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  // Sin worker aparte: se usa el del propio bundle en el hilo principal (vale para fichas de 1-3 páginas
  // y evita servir un fichero de worker y tocar la CSP).
  if (!window.pdfjsWorker) window.pdfjsWorker = await import('pdfjs-dist/legacy/build/pdf.worker');
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data, isEvalSupported: false, disableFontFace: true, useSystemFonts: false }).promise;
  try {
    const lines = [];
    for (let i = 1; i <= Math.min(doc.numPages, maxPages); i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      lines.push(...itemsToLines(content.items));
      lines.push(...otherLeadsLines(content.items));
    }
    return lines.join('\n');
  } finally {
    doc.destroy();
  }
}
